import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TimesheetService, TimesheetEntry, WorkCode } from '../services/timesheet.service';
import { ProjectService, Project } from '../services/project.service';
import { AuthService } from '../services/auth.service';
import { EwoService, Ewo } from '../services/ewo.service';
import { RequestService, Request } from '../services/request.service';
import { SelectedProjectService } from '../services/selectedproject.service';
import { UserService } from '../services/user.service';

interface JobTypeOption {
  code: string;
  description: string;
}

@Component({
  selector: 'app-timesheet',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatIcon],
  templateUrl: './timesheet.component.html',
  styleUrls: ['./timesheet.component.css']
})
export class TimesheetComponent implements OnInit {
  readOnly = false;
  projectNumber: number | null = null;
  workCodes: WorkCode[] = [];
  jobTypes: JobTypeOption[] = [];
  projects: Project[] = [];
  recentEntries: TimesheetEntry[] = [];
  selectedDate: string = new Date().toISOString().split('T')[0];
  selectedProject: number | null = null;
  selectedWorkCode: string = '';
  selectedJobType: string = '';
  hours: number = 0;
  
  loading = false;
  projectsLoading = false;
  jobTypesLoading = false;
  error: string | null = null;
  success: string | null = null;
  
  
  currentEmployeeId: string = '';
  private usernameCache: Map<string, string> = new Map();
  
  // Modal state for delete confirmation
  showDeleteModal: boolean = false;
  pendingDeleteEntry: TimesheetEntry | null = null;
  
  constructor(
    private timesheetService: TimesheetService,
    private projectService: ProjectService,
    private authService: AuthService,
    private ewoService: EwoService,
    private requestService: RequestService,
    private selectedProjectService: SelectedProjectService
    , private userService: UserService
  ) {}

  ngOnInit(): void {
  this.readOnly = this.authService.isReadOnly();
  // Get the logged-in employee ID
  const userId = this.authService.getUserId();
  if (userId) {
    this.currentEmployeeId = userId;
  }
  
  // Load work codes
  this.loadWorkCodes();
  
  // First load projects, then handle selected project
  this.projectsLoading = true;
  this.projectService.getAllProjects().subscribe({
    next: (projects) => {
      this.projects = projects;
      this.projectsLoading = false;
      
      // Now that projects are loaded, set the selected project
      const selectedProject = this.selectedProjectService.getSelectedProject();
      if (selectedProject) {
        this.selectedProject = selectedProject;
        // Load job types for the selected project
        this.onProjectSelected();
      }
    },
    error: (err) => {
      this.error = 'Failed to load projects';
      this.projectsLoading = false;
      console.error(err);
    }
  });
  
  // Also subscribe to future project selections
  this.selectedProjectService.selectedProject$.subscribe(projectNumber => {
    if (projectNumber !== null && projectNumber !== this.selectedProject) {
      this.selectedProject = projectNumber;
      this.onProjectSelected();
    }
  });
}
  
  loadProjects(): void {
    this.projectsLoading = true;
    this.projectService.getAllProjects().subscribe({
      next: (projects) => {
        this.projects = projects;
        this.projectsLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load projects';
        this.projectsLoading = false;
        console.error(err);
      }
    });
  }
  
  loadWorkCodes(): void {
    this.timesheetService.getWorkCodes().subscribe({
      next: (workCodes) => {
        this.workCodes = workCodes.sort((a, b) => a.displayOrder - b.displayOrder);
      },
      error: (err) => {
        this.error = 'Failed to load work codes';
        console.error(err);
      }
    });
  }

  onProjectSelected(): void {
    if (!this.selectedProject) {
      this.jobTypes = [];
      this.recentEntries = [];
      return;
    }

    this.jobTypesLoading = true;
    this.jobTypes = [];
    this.selectedJobType = '';
    
    // Create a combined array of job types from EWOs and RFIs
    this.loadJobTypesForProject(this.selectedProject);
    // Load recent entries for this project
    this.loadRecentEntries(this.selectedProject);
  }

  loadRecentEntries(projectNumber: number): void {
    this.timesheetService.getTimesheetByProject(projectNumber).subscribe({
      next: (entries) => {
        // Sort by date descending and take the first 5
        this.recentEntries = entries
          .slice()
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 5);
      },
      error: (err) => {
        console.error('Failed to load recent entries', err);
        this.recentEntries = [];
      }
    });
  }

  calculateRecentTotal(): number {
    return this.recentEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  }

  private refreshRecent(): void {
    if (this.selectedProject) {
      this.loadRecentEntries(this.selectedProject);
    }
  }

  getUsernameById(userId: string): string {
    if (this.usernameCache.has(userId)) {
      return this.usernameCache.get(userId) || 'Unknown';
    }
    // If not a GUID, use the value directly as a display name
    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidPattern.test(userId)) {
      this.usernameCache.set(userId, userId);
      return userId;
    }
    this.usernameCache.set(userId, 'Loading...');
    this.userService.getUsernameById(userId).subscribe({
      next: (username) => {
        this.usernameCache.set(userId, username);
      },
      error: (err) => {
        console.error(`Failed to load username for ID ${userId}`, err);
        this.usernameCache.set(userId, 'Error loading');
      }
    });
    return this.usernameCache.get(userId) || 'Unknown';
  }

  getWorkCodeDescription(code: string): string {
    const wc = this.workCodes.find(w => w.code === code);
    return wc ? wc.description : code;
  }
  
  loadJobTypesForProject(projectNumber: number): void {
    const jobTypes: JobTypeOption[] = [];

    // Only load EWOs as job types (RFIs are not used as job types)
    this.ewoService.getEwosByProject(projectNumber).subscribe({
      next: (ewos) => {
        ewos.forEach(ewo => {
          jobTypes.push({
            code: `EWO-${ewo.ewoNumber}`,
            description: `EWO #${ewo.ewoNumber}`
          });
        });

        this.jobTypes = jobTypes;
        this.jobTypesLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load EWOs';
        this.jobTypesLoading = false;
        console.error(err);
      }
    });
  }
  
  submitTimesheetEntry(): void {
    if (this.readOnly) {
      this.error = 'This Microsoft SSO session is read-only.';
      return;
    }

    // Basic required checks (allow hours === 0 so we can delete existing entries)
    if (!this.selectedProject || !this.selectedWorkCode) {
      this.error = 'Please fill in all required fields';
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = null;

    const normalizedJobType = this.selectedJobType || null;

    // If hours is 0, ensure an existing same-day entry exists; if so, send a delete (hours=0) upsert
    if (this.hours === 0) {
      this.timesheetService.getTimesheetByProject(this.selectedProject!).subscribe({
        next: (entries) => {
          const existing = entries.find(e => {
            const entryDate = new Date(e.date).toISOString().split('T')[0];
            return entryDate === this.selectedDate &&
              e.employee === this.currentEmployeeId &&
              e.project === this.selectedProject &&
              e.workCode === this.selectedWorkCode &&
              ((e.jobType || null) === normalizedJobType);
          });

          if (!existing) {
            this.error = 'Cannot create an entry with 0 hours';
            this.loading = false;
            return;
          }

          const isoUtcDate = `${this.selectedDate}T00:00:00Z`;
          const deleteEntry: TimesheetEntry = {
            date: isoUtcDate,
            employee: this.currentEmployeeId,
            project: this.selectedProject!,
            workCode: this.selectedWorkCode,
            jobType: normalizedJobType,
            hours: 0
          };

          // Open confirmation modal instead of deleting immediately
          this.pendingDeleteEntry = deleteEntry;
          this.showDeleteModal = true;
          this.loading = false;
          return;
        },
        error: (err) => {
          this.error = err.error || 'Failed to check existing entries';
          this.loading = false;
        }
      });
    }

    // For hours > 0 create a new entry (normalize date to UTC midnight)
    const isoUtcDate = `${this.selectedDate}T00:00:00Z`;
    const entry: TimesheetEntry = {
      date: isoUtcDate,
      employee: this.currentEmployeeId,
      project: this.selectedProject!,
      workCode: this.selectedWorkCode,
      jobType: normalizedJobType,
      hours: this.hours
    };

    this.timesheetService.addTimesheetEntry(entry).subscribe({
      next: () => {
        this.success = 'Time entry added successfully';
        this.loading = false;
        this.selectedWorkCode = '';
        this.selectedJobType = '';
        this.hours = 0;
        this.refreshRecent();
      },
      error: (err) => {
        this.error = err.error || 'Failed to add timesheet entry';
        this.loading = false;
      }
    });
  }

  confirmDelete(): void {
    if (this.readOnly) return;

    if (!this.pendingDeleteEntry) return;
    this.loading = true;
    this.error = null;
    this.showDeleteModal = false;

    this.timesheetService.addTimesheetEntry(this.pendingDeleteEntry).subscribe({
      next: () => {
        this.success = 'Entry deleted';
        this.loading = false;
        this.selectedWorkCode = '';
        this.selectedJobType = '';
        this.hours = 0;
        this.pendingDeleteEntry = null;
        this.refreshRecent();
      },
      error: (err) => {
        this.error = err?.error || err?.message || 'Failed to delete entry';
        this.loading = false;
        this.pendingDeleteEntry = null;
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteModal = false;
    this.pendingDeleteEntry = null;
  }
}
